/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect } from "react";
import Panel from "../Panels/Panel";
import { Spinner, Button } from "react-bootstrap";
import { getConnectReports, getConnectReportsLookupData } from "../../services/ConnectReports";
import { getQueues } from "../../services/ConnectLookups";
import MetricItem from "./MetricItem";
import MetricsConfigModal from "./MetricsConfigModal";
import useLocalState from "../../hooks/useLocalState";
import { isEmptyObject } from "../../utility/index";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";

const COMPONENT_NAME = "CCP components/MetricsDisplay";

const DEFAULT_METRICS = [
	"CONTACTS_IN_QUEUE",
	"OLDEST_CONTACT_AGE",
	"SERVICE_LEVEL_15",
	"QUEUE_ANSWER_TIME",
	"CONTACTS_MISSED",
];

const DEFAULT_TREND_MIN = 5;

const DATA_REFRESH_INTERVAL_SECONDS = 5;

const MetricsDisplay = () => {
	const [queues, setQueues] = useState([]);
	const [metrics, setMetrics] = useState({});
	const [metricLookupData, setMetricLookupData] = useState([]);
	const [queueArn, setQueueArn] = useLocalState("selected-metrics-queue", "ALL");
	const [configModalShow, setConfigModalShow] = useState(false);
	const [metricsConfig, setMetricsConfig] = useLocalState("queue-metrics-config", {
		selectedMetrics: DEFAULT_METRICS,
		trendLengthMinutes: DEFAULT_TREND_MIN,
	});
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState("");
	const trendLength = Math.round((metricsConfig.trendLengthMinutes * 60) / DATA_REFRESH_INTERVAL_SECONDS);

	const getMetricsRenderContent = (metrics, isLoading, error) => {
		if (isLoading) {
			return (
				<Spinner animation="border" className="m-2" role="status">
					<span className="sr-only">Loading...</span>
				</Spinner>
			);
		}

		if (error) {
			return <span style={{ paddingLeft: 15 }}>{error}</span>;
		}

		if (isEmptyObject(metrics)) {
			return <span style={{ paddingLeft: 15 }}>No metrics found.</span>;
		}

		return !isEmptyObject(metrics) && metricLookupData.length
			? metricsConfig.selectedMetrics
					.filter((metric) => metric)
					.map((metric, i) => {
						const info = metricLookupData.find((m) => m.id === metric);
						const data = metrics[metric];

						return <MetricItem metric={{ ...info, data }} trendLength={trendLength} key={`${info.id}_${i}`} />;
					})
			: [];
	};

	const fetchMetrics = async (queueArn) => {
		try {
			setError("");
			const metricsResponse = await getConnectReports(queueArn);
			setMetrics(metricsResponse);
			setIsLoading(false);
		} catch (error) {
			v2.error("Error getting metrics", {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.METRICS_DISPLAY_GET_ERROR,
			});
			setMetrics({});
			setIsLoading(false);
			setError("Could not get queue metrics.");
		}
	};

	const updateMetricsConfig = async (newConfig) => {
		setMetricsConfig(newConfig);
		setConfigModalShow(false);
		await fetchMetrics(queueArn);
	};

	useEffect(() => {
		const fetchLookupData = async () => {
			const queuesResponse = await getQueues();
			setQueues(queuesResponse);

			const lookupDataResponse = await getConnectReportsLookupData();
			setMetricLookupData(lookupDataResponse);
		};

		fetchLookupData();
	}, []);

	useEffect(() => {
		fetchMetrics(queueArn);
		const interval = setInterval(async () => {
			await fetchMetrics(queueArn);
		}, DATA_REFRESH_INTERVAL_SECONDS * 1000);

		return () => {
			clearInterval(interval);
		};
	}, [queueArn]);

	const metricsToDisplay = getMetricsRenderContent(metrics, isLoading, error);

	return (
		<>
			<Panel
				style={{ width: "100%" }}
				title="QUEUE METRICS"
				contentStyle={{ maxHieght: "240px" }}
				headerContent={
					<div className="d-flex">
						<select
							className="form-control form-control-sm"
							value={queueArn}
							onChange={(e) => setQueueArn(e.target.value)}
						>
							<option value="ALL">All queues</option>
							{queues?.map((q) => (
								<option key={q.Id} value={q.Arn}>
									{q.Name}
								</option>
							))}
						</select>
						<Button onClick={() => setConfigModalShow(true)} size="sm" variant="outline-secondary ml-2">
							<i className="fas fa-cog"></i>
						</Button>
					</div>
				}
			>
				<div className="p-1 d-flex flex-wrap justify-content-between" style={{ maxHeight: "240px" }}>
					{metricsToDisplay.length ? metricsToDisplay : <span style={{ padding: 10 }}>No metrics to display</span>}
				</div>
			</Panel>
			<MetricsConfigModal
				show={configModalShow}
				metricsConfig={metricsConfig}
				updateMetricsConfig={updateMetricsConfig}
				onHide={() => setConfigModalShow(false)}
			/>
		</>
	);
};

export default MetricsDisplay;
